import { useMemo, useState } from "react";
import { latestArticles } from "@/mocks/positiveNews";

const tagOptions = ["全部", "好人好事", "高光时刻", "公益现场", "暖心人间", "凡人微光"];

export default function LatestFeed() {
  const [active, setActive] = useState("全部");
  const [query, setQuery] = useState("");
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const filtered = useMemo(() => {
    return latestArticles.filter((a) => {
      const matchTag = active === "全部" || a.tag === active;
      const matchQuery = !query.trim() || a.title.includes(query.trim()) || a.summary.includes(query.trim());
      return matchTag && matchQuery;
    });
  }, [active, query]);

  return (
    <section id="hot" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* 区块头 */}
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" />
              最新热点 · RED STORIES
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">今日值得点赞的红料</h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">滚烫而真实的瞬间，来自这个国家每天发生的普通人善意。</p>
          </div>

          <div className="relative w-full md:w-72">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索标题或内容…"
              aria-label="搜索红料"
              className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-11 pr-4 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        {/* 分类切换 */}
        <div className="mt-6 flex flex-wrap gap-2">
          {tagOptions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActive(t)}
              className={`whitespace-nowrap cursor-pointer rounded-full px-4 py-1.5 text-sm transition-colors ${
                active === t
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* 文章卡片 */}
        {filtered.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-inbox-line mb-3 text-3xl text-foreground-300" />
            <p className="text-sm text-foreground-600">没有找到相关内容，换个关键词试试吧。</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <article
                key={a.id}
                className="group overflow-hidden rounded-2xl border border-background-200 bg-background-100 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative h-48 w-full overflow-hidden md:h-52">
                  <img
                    src={a.img}
                    alt={a.title}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 whitespace-nowrap rounded-full bg-primary-500/90 px-3 py-1 text-xs text-background-50 backdrop-blur">
                    {a.tag}
                  </span>
                  <span className="absolute bottom-3 right-3 rounded-full bg-background-950/50 px-2.5 py-1 text-[11px] text-background-50 backdrop-blur">
                    {a.date}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading line-clamp-2 min-h-[3.5rem] text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.summary}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" />
                        {a.read}
                      </span>
                      <button
                        type="button"
                        onClick={() => setLiked((p) => ({ ...p, [a.id]: !p[a.id] }))}
                        aria-pressed={!!liked[a.id]}
                        className={`flex cursor-pointer items-center gap-1 transition-colors ${
                          liked[a.id] ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                        }`}
                      >
                        <i className={liked[a.id] ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                        {(a.likes + (liked[a.id] ? 1 : 0)).toLocaleString()}
                      </button>
                    </span>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                      <i className="ri-arrow-right-up-line text-base" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}